import dayjs from 'dayjs';
import {
  activeLookupValues,
  buildComplaintMatterPayload,
  caseStatusOptions as fallbackCaseStatusOptions,
  codeForLookupDisplay,
  displayForLookupCode,
  firstValue,
  isFieldRecord,
  lookupCode,
  lookupDisplay,
  mapComplaintActivityToEntry,
  textFromValue,
  toProtobufTimestamp,
} from './complaintIntakeUtils';
import type {
  ActivityLogEntry,
  CaseStatus,
  ComplaintCodeValue,
  ComplaintIntakeData,
  ComplaintLookupSet,
  ComplaintMatterPayload,
  ComplaintFormState,
  FieldRecord,
  ResolutionData,
  RiskAssessment,
  RiskLevel,
} from './complaintIntakeUtils';
import type {
  CaseDetailRecord,
  CaseDocumentRecord,
  CaseFieldRecord,
  CaseFieldOptionRecord,
  CaseFieldValueInput,
  CaseFieldValueRecord,
  CaseHeaderRecord,
  CaseInputPayload,
  CaseSummaryRecord,
  CaseTypeDefinitionRecord,
  CaseTypeFieldRecord,
  CaseTypeRecord,
} from './caseManagementTypes';

export const COMPLAINT_CASE_TYPE_NAME = 'Complaints';

const dateFromTimestamp = (value: unknown) => {
  const text = textFromValue(value);
  if (!text) return '';

  const dateOnly = text.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (dateOnly) return dateOnly;

  const parsed = dayjs(text);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : text.slice(0, 10);
};

export const normalizeCaseTypeName = (value: unknown) =>
  textFromValue(value)
    .toLowerCase()
    .replaceAll(/[^\da-z]+/g, '');

export const isComplaintCaseTypeName = (value: unknown) =>
  ['complaint', normalizeCaseTypeName(COMPLAINT_CASE_TYPE_NAME)].includes(
    normalizeCaseTypeName(value)
  );

export const numberFromValue = (value: unknown) => {
  const text = textFromValue(value);
  if (!text) return;

  const numeric = Number(text);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
};

export const completeDateForCaseStatusChange = (
  currentStatusCode: string,
  nextStatusCode: string,
  currentCompleteDt: string,
  today: string
) => {
  const wasClosed = currentStatusCode.trim().toUpperCase() === 'CLOSED';
  const isClosed = nextStatusCode.trim().toUpperCase() === 'CLOSED';

  if (isClosed && !wasClosed) return today;
  if (!isClosed && wasClosed) return '';
  return currentCompleteDt;
};

export type GenericCaseStatusOption = {
  code: string;
  display: string;
};

const fallbackCaseStatusCodeForDisplay = (display: string) =>
  textFromValue(display).trim().toUpperCase().replaceAll(' ', '_');

const fallbackDisplayForCaseStatusCode = (code: string) =>
  textFromValue(code)
    .trim()
    .toLowerCase()
    .replaceAll(/[_-]+/g, ' ')
    .replaceAll(/\b\w/g, (letter) => letter.toUpperCase());

const caseStatusLookupDisplay = (value?: ComplaintCodeValue) =>
  textFromValue(value?.displayName) ||
  textFromValue(value?.display_name) ||
  textFromValue(value?.description) ||
  textFromValue(value?.Description) ||
  lookupDisplay(value);

export const caseStatusCodeForDisplay = (lookups: ComplaintLookupSet, statusDisplay: string) => {
  const normalizedDisplay = textFromValue(statusDisplay).trim().toLowerCase();
  const matchingStatus = (lookups.caseStatuses || []).find(
    (status) => caseStatusLookupDisplay(status).trim().toLowerCase() === normalizedDisplay
  );

  return (
    lookupCode(matchingStatus) ||
    codeForLookupDisplay(lookups.caseStatuses, statusDisplay) ||
    fallbackCaseStatusCodeForDisplay(statusDisplay)
  );
};

export const caseStatusDisplayForCode = (lookups: ComplaintLookupSet, statusCode: string) => {
  const normalizedStatusCode = textFromValue(statusCode).trim().toUpperCase();
  const matchingStatus = (lookups.caseStatuses || []).find(
    (status) => lookupCode(status).toUpperCase() === normalizedStatusCode
  );
  const fallbackDisplay = fallbackCaseStatusOptions.find(
    (display) => fallbackCaseStatusCodeForDisplay(display) === normalizedStatusCode
  );

  return (
    caseStatusLookupDisplay(matchingStatus) ||
    displayForLookupCode(lookups.caseStatuses, normalizedStatusCode) ||
    fallbackDisplay ||
    fallbackDisplayForCaseStatusCode(normalizedStatusCode)
  );
};

export const defaultGenericCaseStatusCode = (lookups: ComplaintLookupSet) =>
  caseStatusCodeForDisplay(lookups, fallbackCaseStatusOptions[0]);

export const allowedGenericCaseStatusOptions = (
  lookups: ComplaintLookupSet,
  currentStatusCode: string
): GenericCaseStatusOption[] => {
  const statuses = activeLookupValues(lookups.caseStatuses);
  const normalizedCurrentCode = textFromValue(currentStatusCode).trim().toUpperCase();

  if (statuses.length === 0) {
    return fallbackCaseStatusOptions.map((display) => ({
      code: fallbackCaseStatusCodeForDisplay(display),
      display,
    }));
  }

  if (!normalizedCurrentCode) {
    return statuses.map((status) => ({
      code: lookupCode(status),
      display: caseStatusLookupDisplay(status),
    }));
  }

  const allowedCodes = new Set([normalizedCurrentCode]);

  for (const transition of lookups.statusTransitions || []) {
    const active = transition.active ?? transition.isActive ?? transition.is_active ?? true;
    const fromStatusCode = textFromValue(
      transition.fromStatusCode || transition.from_status_code
    ).toUpperCase();
    const toStatusCode = textFromValue(
      transition.toStatusCode || transition.to_status_code
    ).toUpperCase();

    if (active === false || textFromValue(active).toLowerCase() === 'false') continue;
    if (fromStatusCode === normalizedCurrentCode && toStatusCode) {
      allowedCodes.add(toStatusCode);
    }
  }

  const allowedStatuses = statuses
    .filter((status) => allowedCodes.has(lookupCode(status).toUpperCase()))
    .map((status) => ({
      code: lookupCode(status),
      display: caseStatusLookupDisplay(status),
    }))
    .filter((status) => status.code);

  if (
    normalizedCurrentCode &&
    !allowedStatuses.some((status) => status.code.toUpperCase() === normalizedCurrentCode)
  ) {
    return [
      {
        code: normalizedCurrentCode,
        display: caseStatusDisplayForCode(lookups, normalizedCurrentCode),
      },
      ...allowedStatuses,
    ];
  }

  return allowedStatuses;
};

export const idFromRecord = (record: FieldRecord | undefined, keys: string[]) =>
  numberFromValue(firstValue(record, keys));

export const unwrapCaseResponse = (payload: unknown): unknown => {
  if (!isFieldRecord(payload)) return payload;

  const data = payload.data || payload.Data;
  if (data !== undefined) return unwrapCaseResponse(data);

  const response = payload.response || payload.Response;
  if (response !== undefined) return unwrapCaseResponse(response);

  return payload;
};

const asRecord = (value: unknown): FieldRecord | undefined =>
  isFieldRecord(value) ? value : undefined;

const asArray = <T>(value: unknown): T[] =>
  Array.isArray(value) ? (value.filter((item) => isFieldRecord(item)) as T[]) : [];

export const getCaseTypeId = (caseType?: CaseTypeRecord) =>
  idFromRecord(asRecord(caseType), ['id', 'Id', 'caseTypeId', 'case_type_id']);

export const getCaseTypeName = (caseType?: CaseTypeRecord) =>
  firstValue(asRecord(caseType), ['name', 'Name']);

export const getCaseTypeFromDefinition = (definition?: CaseTypeDefinitionRecord) =>
  asRecord(definition?.caseType) ||
  asRecord(definition?.case_type) ||
  asRecord(definition?.CaseType);

export const getFieldsFromDefinition = (
  definition?: CaseTypeDefinitionRecord
): CaseTypeFieldRecord[] => asArray<CaseTypeFieldRecord>(definition?.fields || definition?.Fields);

export const getCaseTypeFieldId = (field?: CaseTypeFieldRecord) =>
  idFromRecord(asRecord(field), ['id', 'Id', 'caseTypeFieldId', 'case_type_field_id']);

export const getCaseField = (field?: CaseTypeFieldRecord) =>
  (asRecord(field?.field) || asRecord(field?.Field)) as CaseFieldRecord | undefined;

export const getCaseFieldName = (field?: CaseTypeFieldRecord) =>
  firstValue(asRecord(getCaseField(field)), ['name', 'Name']);

export const getCaseFieldDataType = (field?: CaseTypeFieldRecord) =>
  firstValue(asRecord(getCaseField(field)), ['dataType', 'DataType', 'data_type']).toLowerCase();

const INLINE_COMPLAINT_FIELD_NAMES = new Set(['root_cause', 'root_cause_description']);

export const getInlineComplaintCaseFields = (definition?: CaseTypeDefinitionRecord) =>
  getFieldsFromDefinition(definition).filter(
    (field) =>
      isCaseTypeFieldActive(field) &&
      isSupportedCaseField(field) &&
      INLINE_COMPLAINT_FIELD_NAMES.has(normalizeCaseFieldName(getCaseFieldName(field)))
  );

export const getCaseFieldOptions = (
  field?: CaseTypeFieldRecord,
  lookupGroups?: Record<string, ComplaintCodeValue[]>
): CaseFieldOptionRecord[] => {
  const caseField = asRecord(getCaseField(field)) || {};
  const configuredOptions = asArray<CaseFieldOptionRecord>(caseField.options || caseField.Options);
  if (configuredOptions.length > 0) return configuredOptions;

  const lookupTypeCode = firstValue(caseField, [
    'lookupTypeCode',
    'lookup_type_code',
  ]).toUpperCase();
  return (lookupGroups?.[lookupTypeCode] || []).map((value) => ({
    code: lookupCode(value),
    displayName: lookupDisplay(value),
    description: value.description || value.Description,
    sortOrder: value.sortOrder || value.sort_order,
    active: value.active ?? value.isActive ?? value.is_active,
  }));
};

export const getCaseTypeDefinitionsFromResponse = (
  payload: unknown
): CaseTypeDefinitionRecord[] => {
  const source = unwrapCaseResponse(payload);
  if (!isFieldRecord(source)) return [];

  const definitions = source.definitions || source.Definitions;
  if (Array.isArray(definitions)) {
    return definitions.filter((definition) => isFieldRecord(definition));
  }

  const definition = source.definition || source.Definition;
  return isFieldRecord(definition) ? [definition] : [];
};

export type CaseTypeDefinitionLoaderApi = {
  listCaseTypes: () => Promise<unknown>;
  getCaseTypeDefinition: (caseTypeId: string | number) => Promise<unknown>;
};

export const loadCaseTypeDefinitions = async (
  caseApi: CaseTypeDefinitionLoaderApi
): Promise<CaseTypeDefinitionRecord[]> => {
  const caseTypesResponse = await caseApi.listCaseTypes();
  const caseTypes = getCaseTypesFromResponse(caseTypesResponse);
  const definitions = await Promise.all(
    caseTypes.map(async (caseType) => {
      const caseTypeId = getCaseTypeId(caseType);
      if (!caseTypeId) return;

      const definitionResponse = await caseApi.getCaseTypeDefinition(caseTypeId);
      return getCaseTypeDefinitionsFromResponse(definitionResponse)[0];
    })
  );

  return definitions.filter((definition): definition is CaseTypeDefinitionRecord =>
    isFieldRecord(definition)
  );
};

export const getComplaintCaseDefinition = (definitions: CaseTypeDefinitionRecord[]) =>
  definitions.find((definition) =>
    isComplaintCaseTypeName(getCaseTypeName(getCaseTypeFromDefinition(definition)))
  );

export const getCaseTypesFromResponse = (payload: unknown): CaseTypeRecord[] => {
  const source = unwrapCaseResponse(payload);
  if (!isFieldRecord(source)) return [];

  const caseTypes = source.caseTypes || source.case_types || source.CaseTypes;
  return Array.isArray(caseTypes) ? caseTypes.filter((caseType) => isFieldRecord(caseType)) : [];
};

export const getCaseDetailFromResponse = (payload: unknown): CaseDetailRecord | undefined => {
  const source = unwrapCaseResponse(payload);
  if (!isFieldRecord(source)) return;
  if (
    source.definition ||
    source.Definition ||
    source.values ||
    source.Values ||
    source.activities ||
    source.Activities ||
    source.documents ||
    source.Documents
  ) {
    return source;
  }

  const detail = source.case || source.Case;
  return isFieldRecord(detail) ? detail : source;
};

export const getCaseHeader = (detailOrSummary?: CaseDetailRecord | CaseSummaryRecord) =>
  (asRecord(detailOrSummary?.case) || asRecord(detailOrSummary?.Case)) as
    | CaseHeaderRecord
    | undefined;

export const getCaseId = (caseHeader?: CaseHeaderRecord) =>
  idFromRecord(asRecord(caseHeader), ['id', 'Id', 'caseId', 'case_id']);

export const getCaseHeaderTypeName = (caseHeader?: CaseHeaderRecord) =>
  firstValue(asRecord(caseHeader), ['caseTypeName', 'CaseTypeName', 'case_type_name']);

export const getCaseHeaderTypeId = (caseHeader?: CaseHeaderRecord) =>
  idFromRecord(asRecord(caseHeader), ['caseTypeId', 'CaseTypeId', 'case_type_id']);

export const getCaseSummariesFromResponse = (payload: unknown): CaseSummaryRecord[] => {
  const source = unwrapCaseResponse(payload);
  if (!isFieldRecord(source)) return [];

  const cases = source.cases || source.Cases;
  return Array.isArray(cases) ? cases.filter((caseSummary) => isFieldRecord(caseSummary)) : [];
};

export const getCaseNextPageTokenFromResponse = (payload: unknown) => {
  const source = unwrapCaseResponse(payload);
  return firstValue(asRecord(source), ['nextPageToken', 'NextPageToken', 'next_page_token']);
};

export const getCaseDisplayFields = (summary?: CaseSummaryRecord) => {
  const fields = summary?.displayFields || summary?.display_fields || summary?.DisplayFields;
  return isFieldRecord(fields) ? (fields as Record<string, string>) : {};
};

export const getCaseSummaryLastActivityNote = (summary?: CaseSummaryRecord) => {
  const activity = getCaseSummaryLastActivity(summary);
  return (
    firstValue(activity, ['note', 'Note']) ||
    firstValue(asRecord(summary), ['lastActivityNote', 'LastActivityNote', 'last_activity_note'])
  );
};

export const getCaseSummaryLastActivity = (summary?: CaseSummaryRecord) => {
  const activity =
    asRecord(summary)?.lastActivity ||
    asRecord(summary)?.LastActivity ||
    asRecord(summary)?.last_activity;
  return isFieldRecord(activity) ? activity : undefined;
};

export const getCaseFieldValue = (value: CaseFieldValueRecord | undefined) => {
  if (!value) return '';
  const record = asRecord(value);

  return (
    firstValue(record, ['textValue', 'TextValue', 'text_value']) ||
    firstValue(record, ['numberValue', 'NumberValue', 'number_value']) ||
    dateFromTimestamp(firstValue(record, ['dateValue', 'DateValue', 'date_value']))
  );
};

export const normalizeCaseFieldName = (value: unknown) =>
  textFromValue(value)
    .trim()
    .replaceAll(/([a-z\d])([A-Z])/g, '$1_$2')
    .replaceAll(/[^\da-zA-Z]+/g, '_')
    .replaceAll(/^_+|_+$/g, '')
    .toLowerCase();

export const getCaseFieldValuesByName = (detail?: CaseDetailRecord): Record<string, string> => {
  const values = asArray<CaseFieldValueRecord>(detail?.values || detail?.Values);
  const fieldValues: Record<string, string> = {};

  for (const value of values) {
    const name = normalizeCaseFieldName(
      firstValue(asRecord(value), ['fieldName', 'FieldName', 'field_name'])
    );
    const display = getCaseFieldValue(value);
    if (!name || !display) continue;

    fieldValues[name] = fieldValues[name] ? `${fieldValues[name]}, ${display}` : display;
  }

  return fieldValues;
};

const complaintPayloadFieldByCaseField: Record<string, keyof ComplaintMatterPayload> = {
  complainant_first: 'complainantFirst',
  complainant_last: 'complainantLast',
  complainant_type_code: 'complainantTypeCode',
  complainant_contact_email: 'complainantContactEmail',
  complainant_contact_phone: 'complainantContactPhone',
  department_code: 'departmentCode',
  severity_code: 'severityCode',
  complaint_type_code: 'complaintTypeCode',
  contact_source_code: 'contactSourceCode',
  received_date: 'receivedDate',
  description: 'description',
  action_steps: 'actionSteps',
  risk_level_code: 'riskLevelCode',
  resolution_details: 'resolutionDetails',
};

const supplementalComplaintFieldValue = (
  fieldName: string,
  formData: ComplaintIntakeData,
  matterPayload: ComplaintMatterPayload
) => {
  switch (normalizeCaseFieldName(fieldName)) {
    case 'legacy_complaint_matter_id': {
      return '';
    }
    case 'phone': {
      return formData.complainantContactPhone;
    }
    case 'counselink_id': {
      return formData.counselinkId;
    }
    case 'work_type_code': {
      return formData.workTypeCode;
    }
    case 'risk_flag_police': {
      return matterPayload.riskIndicators.police ? 'true' : '';
    }
    case 'risk_flag_media': {
      return matterPayload.riskIndicators.media ? 'true' : '';
    }
    case 'risk_flag_safety': {
      return matterPayload.riskIndicators.safety ? 'true' : '';
    }
    case 'risk_flag_attorney': {
      return matterPayload.riskIndicators.attorney ? 'true' : '';
    }
    default: {
      return;
    }
  }
};

const FIXED_COMPLAINT_CASE_FIELD_NAMES = new Set([
  'action_steps',
  'assigned_to_user',
  'case_status_code',
  'client_code',
  'closed_date',
  'complainant_contact_email',
  'complainant_contact_phone',
  'complainant_first',
  'complainant_last',
  'complainant_type_code',
  'complaint_matter_id',
  'complaint_type_code',
  'contact_source_code',
  'counselink_id',
  'department_code',
  'description',
  'follow_up_date',
  'legacy_complaint_matter_id',
  'order_number',
  'phone',
  'property_id',
  'property_loan_id',
  'received_date',
  'resolution_details',
  'risk_flag_attorney',
  'risk_flag_media',
  'risk_flag_police',
  'risk_flag_safety',
  'risk_level_code',
  'row_version',
  'severity_code',
  'spi_property_id',
  'vendor_code',
  'work_order_id',
  'work_type_code',
]);

export const isFixedComplaintCaseFieldName = (fieldName: string) =>
  FIXED_COMPLAINT_CASE_FIELD_NAMES.has(normalizeCaseFieldName(fieldName));

const isExplicitFalse = (value: unknown) =>
  value === false || textFromValue(value).toLowerCase() === 'false';

export const isCaseTypeFieldActive = (field?: CaseTypeFieldRecord) => {
  const typeField = asRecord(field);
  const caseField = asRecord(getCaseField(field));

  return (
    !isExplicitFalse(typeField?.active) &&
    !isExplicitFalse(typeField?.Active) &&
    !isExplicitFalse(caseField?.active) &&
    !isExplicitFalse(caseField?.Active)
  );
};

export const getCaseTypeFieldParentId = (field?: CaseTypeFieldRecord) =>
  idFromRecord(asRecord(field), [
    'parentCaseTypeFieldId',
    'ParentCaseTypeFieldId',
    'parent_case_type_field_id',
  ]);

export const isGroupCaseField = (field?: CaseTypeFieldRecord) =>
  getCaseFieldDataType(field) === 'group';

export const isCaseTypeFieldMultiple = (field?: CaseTypeFieldRecord) => {
  const record = asRecord(field);
  const value = record?.multiple ?? record?.Multiple;
  return value === true || textFromValue(value).toLowerCase() === 'true';
};

export const isSupportedCaseField = (field?: CaseTypeFieldRecord) => {
  const dataType = getCaseFieldDataType(field);
  return dataType === 'text' || dataType === 'number' || dataType === 'date' || dataType === 'enum';
};

export const getAdditionalComplaintCaseFields = (definition?: CaseTypeDefinitionRecord) =>
  getFieldsFromDefinition(definition).filter(
    (field) =>
      isCaseTypeFieldActive(field) &&
      isSupportedCaseField(field) &&
      !isFixedComplaintCaseFieldName(getCaseFieldName(field))
  );

export const hasComplaintCaseField = (
  definition: CaseTypeDefinitionRecord | undefined,
  fieldName: string
) => {
  const normalizedFieldName = normalizeCaseFieldName(fieldName);

  return getFieldsFromDefinition(definition).some(
    (field) =>
      isCaseTypeFieldActive(field) &&
      isSupportedCaseField(field) &&
      normalizeCaseFieldName(getCaseFieldName(field)) === normalizedFieldName
  );
};

export const additionalCaseFieldValuesById = (
  definition: CaseTypeDefinitionRecord | undefined,
  detail?: CaseDetailRecord
): Record<string, string | string[]> => {
  const additionalFieldIds = new Set(
    getFieldsFromDefinition(definition)
      .filter(
        (field) =>
          isCaseTypeFieldActive(field) &&
          isSupportedCaseField(field) &&
          (!isFixedComplaintCaseFieldName(getCaseFieldName(field)) ||
            (getCaseFieldDataType(field) === 'enum' && isCaseTypeFieldMultiple(field)))
      )
      .map((field) => getCaseTypeFieldId(field))
      .filter(Boolean)
      .map(String)
  );
  const values = caseTypeFieldValuesById(detail, definition);
  const result: Record<string, string | string[]> = {};

  for (const [fieldId, value] of Object.entries(values)) {
    if (additionalFieldIds.has(fieldId)) result[fieldId] = value;
  }

  return result;
};

export const buildAdditionalCaseFieldSnapshot = (
  valuesByFieldId: Record<string, string | string[]> = {}
) =>
  JSON.stringify(
    Object.entries(valuesByFieldId)
      .map(([fieldId, value]) => [
        fieldId,
        (Array.isArray(value) ? value : [value]).map(textFromValue).filter(Boolean),
      ])
      .filter(([, values]) => (values as string[]).length > 0)
      .sort(([first], [second]) => String(first).localeCompare(String(second)))
  );

export const buildCaseFieldValueInput = (
  field: CaseTypeFieldRecord,
  value: unknown,
  options: { includeEmpty?: boolean } = {}
): CaseFieldValueInput | CaseFieldValueInput[] | undefined => {
  const fieldId = getCaseTypeFieldId(field);
  const dataType = getCaseFieldDataType(field);
  if (!fieldId || !isSupportedCaseField(field)) return;

  if (dataType === 'number') {
    const textValue = textFromValue(value);
    if (!textValue && options.includeEmpty) {
      return { caseTypeFieldId: fieldId, occurrence: 1 };
    }

    const numberValue = Number(textValue);
    return Number.isFinite(numberValue)
      ? { caseTypeFieldId: fieldId, occurrence: 1, numberValue }
      : undefined;
  }

  if (dataType === 'date') {
    const textValue = textFromValue(value);
    if (!textValue && options.includeEmpty) {
      return { caseTypeFieldId: fieldId, occurrence: 1 };
    }

    const dateValue = toProtobufTimestamp(textValue);
    return dateValue ? { caseTypeFieldId: fieldId, occurrence: 1, dateValue } : undefined;
  }

  if (dataType === 'enum' || (dataType === 'text' && isCaseTypeFieldMultiple(field) && Array.isArray(value))) {
    if (isCaseTypeFieldMultiple(field) && Array.isArray(value)) {
      const codes = value.map(textFromValue).filter(Boolean);
      return codes.length
        ? codes.map((textValue, index) => ({
            caseTypeFieldId: fieldId,
            occurrence: index + 1,
            textValue,
          }))
        : options.includeEmpty
          ? [{ caseTypeFieldId: fieldId, occurrence: 1 }]
          : [];
    }
    const textValue = textFromValue(value);
    if (!textValue && options.includeEmpty) {
      return { caseTypeFieldId: fieldId, occurrence: 1 };
    }
    return textValue || options.includeEmpty
      ? { caseTypeFieldId: fieldId, occurrence: 1, textValue }
      : undefined;
  }

  const textValue = textFromValue(value);
  return textValue || options.includeEmpty
    ? { caseTypeFieldId: fieldId, occurrence: 1, textValue }
    : undefined;
};

export const buildComplaintCaseInput = ({
  definition,
  formData,
  resolutionData,
  riskAssessment,
  caseStatus,
  lookups,
  additionalFieldValuesById = {},
}: {
  definition: CaseTypeDefinitionRecord;
  formData: ComplaintIntakeData;
  resolutionData: ResolutionData;
  riskAssessment: RiskAssessment;
  caseStatus: CaseStatus;
  lookups: ComplaintLookupSet;
  additionalFieldValuesById?: Record<string, string | string[]>;
}): CaseInputPayload => {
  const caseTypeId = getCaseTypeId(getCaseTypeFromDefinition(definition)) || 0;
  const matterPayload = buildComplaintMatterPayload(
    formData,
    resolutionData,
    riskAssessment,
    caseStatus,
    lookups
  );
  const fieldValues = getFieldsFromDefinition(definition)
    .flatMap((field) => {
      if (!isCaseTypeFieldActive(field) || !isSupportedCaseField(field)) return;

      const fieldId = getCaseTypeFieldId(field);
      const fieldName = getCaseFieldName(field);
      const additionalFieldKey = fieldId ? String(fieldId) : '';
      const hasAdditionalValue =
        additionalFieldKey &&
        Object.prototype.hasOwnProperty.call(additionalFieldValuesById, additionalFieldKey);
      const payloadField = complaintPayloadFieldByCaseField[normalizeCaseFieldName(fieldName)];
      const supplementalValue = supplementalComplaintFieldValue(fieldName, formData, matterPayload);
      let payloadValue: unknown = supplementalValue;
      if (payloadField) {
        payloadValue = matterPayload[payloadField];
      }
      if (hasAdditionalValue) {
        payloadValue = additionalFieldValuesById[additionalFieldKey];
      }
      const shouldIncludeEmpty =
        Boolean(hasAdditionalValue) || Boolean(payloadField) || supplementalValue !== undefined;

      if (normalizeCaseFieldName(fieldName) === 'complaint_type_code') {
        const codes = (
          formData.complaintTypeCodes?.length
            ? formData.complaintTypeCodes
            : [matterPayload.complaintTypeCode]
        )
          .map((code) => code.trim())
          .filter(Boolean);
        return codes.length
          ? codes.map((code, index) => ({
              caseTypeFieldId: fieldId,
              occurrence: index + 1,
              textValue: code,
            }))
          : [{ caseTypeFieldId: fieldId, occurrence: 1 }];
      }

      const value = buildCaseFieldValueInput(field, payloadValue, {
        includeEmpty: shouldIncludeEmpty,
      });
      return Array.isArray(value) ? value : value ? [value] : [];
    })
    .filter(Boolean) as CaseFieldValueInput[];

  return {
    caseTypeId,
    propertyId: numberFromValue(formData.spiPropertyId),
    propertyLoanId: numberFromValue(formData.propertyLoanId),
    orderNumber: numberFromValue(formData.orderNumber),
    vendorCode: formData.vendorCode.trim(),
    assignedToUser: matterPayload.assignedToUser,
    caseStatusCode: matterPayload.caseStatusCode,
    clientCode: formData.clientLabel.trim(),
    followUpDt: matterPayload.followUpDate,
    fieldValues,
  };
};

const mergeNonEmpty = <T extends object>(base: T, next: Partial<T>) => {
  const merged = { ...base };

  for (const [key, value] of Object.entries(next)) {
    if (textFromValue(value)) {
      merged[key as keyof T] = textFromValue(value) as T[keyof T];
    }
  }

  return merged;
};

const displayFromCode = (
  lookups: ComplaintLookupSet,
  lookupKey: keyof ComplaintLookupSet,
  code: unknown
) => {
  const normalizedCode = textFromValue(code);
  const fallbackDisplay = normalizedCode
    .trim()
    .toLowerCase()
    .replaceAll(/[_-]+/g, ' ')
    .replaceAll(/\b\w/g, (letter) => letter.toUpperCase());

  return displayForLookupCode(lookups[lookupKey] as any, normalizedCode) || fallbackDisplay;
};

export const mapCaseDetailToComplaintState = (
  payload: unknown,
  base: ComplaintFormState,
  lookups: ComplaintLookupSet
): ComplaintFormState => {
  const detail = getCaseDetailFromResponse(payload);
  const header = getCaseHeader(detail);
  const fields = getCaseFieldValuesByName(detail);
  const complaintTypeCodes = asArray<CaseFieldValueRecord>(detail?.values || detail?.Values)
    .filter(
      (value) =>
        normalizeCaseFieldName(value.fieldName || value.field_name) === 'complaint_type_code'
    )
    .sort((first, second) => Number(first.occurrence || 0) - Number(second.occurrence || 0))
    .map((value) => textFromValue(value.textValue || value.text_value).trim())
    .filter(Boolean);
  const multipleFieldValues = caseTypeFieldValuesById(
    detail,
    detail?.definition || detail?.Definition
  );
  const multipleFieldForName = (name: string) =>
    getFieldsFromDefinition(detail?.definition || detail?.Definition).find(
      (field) =>
        normalizeCaseFieldName(getCaseFieldName(field)) === name && isCaseTypeFieldMultiple(field)
    );
  const selectedCodes = (name: string) => {
    const fieldId = getCaseTypeFieldId(multipleFieldForName(name));
    const values = fieldId ? multipleFieldValues[String(fieldId)] : undefined;
    return Array.isArray(values) ? values : [];
  };
  const complainantTypeCodes = selectedCodes('complainant_type_code');
  const contactSourceCodes = selectedCodes('contact_source_code');
  const departmentCodes = selectedCodes('department_code');
  const severityCodes = selectedCodes('severity_code');
  const caseId = getCaseId(header);
  const caseStatusCode =
    firstValue(asRecord(header), ['caseStatusCode', 'CaseStatusCode', 'case_status_code']) ||
    fields.case_status_code;
  const riskLevelCode = fields.risk_level_code;
  const actionSteps = fields.action_steps;
  const activities = asArray(detail?.activities || detail?.Activities)
    .map((activity, index) => mapComplaintActivityToEntry(activity, lookups, index))
    .filter(Boolean) as ActivityLogEntry[];

  return {
    formData: {
      ...mergeNonEmpty(base.formData, {
        complaintId: caseId ? String(caseId) : '',
        rowVersion:
          firstValue(asRecord(header), [
            'rowVersion',
            'RowVersion',
            'row_version',
            'complaintRowVersion',
            'ComplaintRowVersion',
            'complaint_row_version',
            'complaintMatterRowVersion',
            'ComplaintMatterRowVersion',
            'complaint_matter_row_version',
            'matterRowVersion',
            'MatterRowVersion',
            'matter_row_version',
          ]) ||
          fields.row_version ||
          fields.rowVersion ||
          fields.RowVersion ||
          fields.complaint_row_version ||
          fields.complaintRowVersion ||
          fields.ComplaintRowVersion ||
          fields.complaint_matter_row_version ||
          fields.complaintMatterRowVersion ||
          fields.ComplaintMatterRowVersion ||
          fields.matter_row_version ||
          fields.matterRowVersion ||
          fields.MatterRowVersion ||
          firstValue(asRecord(header), ['modifyDt', 'ModifyDt', 'modify_dt']),
        orderNumber: firstValue(asRecord(header), ['orderNumber', 'OrderNumber', 'order_number']),
        propertyLoanId: firstValue(asRecord(header), [
          'propertyLoanId',
          'PropertyLoanId',
          'propertyLoanID',
          'PropertyLoanID',
          'property_loan_id',
        ]),
        spiPropertyId: firstValue(asRecord(header), [
          'propertyId',
          'PropertyId',
          'propertyID',
          'PropertyID',
          'property_id',
          'spiPropertyId',
          'SpiPropertyId',
          'SPIPropertyId',
          'spi_property_id',
        ]),
        vendorCode:
          firstValue(asRecord(header), ['vendorCode', 'VendorCode', 'vendor_code']) ||
          fields.vendor_code,
        clientLabel: firstValue(asRecord(header), ['clientCode', 'ClientCode', 'client_code']),
        complainantFirst: fields.complainant_first,
        complainantLast: fields.complainant_last,
        complainantTypeCode: complainantTypeCodes[0] || fields.complainant_type_code,
        complainantContactEmail: fields.complainant_contact_email,
        complainantContactPhone: fields.complainant_contact_phone || fields.phone,
        counselinkId: fields.counselink_id,
        departmentCode: departmentCodes[0] || fields.department_code,
        severityCode: severityCodes[0] || fields.severity_code,
        workTypeCode: fields.work_type_code,
        complaintTypeCode: fields.complaint_type_code,
        contactSourceCode: contactSourceCodes[0] || fields.contact_source_code,
        receivedDate: fields.received_date,
        description: fields.description,
        demand: fields.demand,
        assignedTo:
          firstValue(asRecord(header), ['assignedToUser', 'AssignedToUser', 'assigned_to_user']) ||
          fields.assigned_to_user,
      }),
      complaintTypeCodes,
      complaintTypeCode: complaintTypeCodes[0] || base.formData.complaintTypeCode,
      complainantTypeCodes,
      contactSourceCodes,
      departmentCodes,
      severityCodes,
    },
    resolutionData: mergeNonEmpty(base.resolutionData, {
      followUpDate:
        dateFromTimestamp(
          firstValue(asRecord(header), ['followUpDt', 'FollowUpDt', 'follow_up_dt'])
        ) || fields.follow_up_date,
      actionSteps,
      resolutionDetails: fields.resolution_details,
    }),
    caseStatus:
      (displayFromCode(lookups, 'caseStatuses', caseStatusCode) as CaseStatus) || base.caseStatus,
    riskLevel:
      (displayFromCode(lookups, 'riskLevels', riskLevelCode) as RiskLevel) || base.riskLevel,
    activityLog: activities.length > 0 ? activities : base.activityLog,
    additionalFieldValues: additionalCaseFieldValuesById(
      detail?.definition || detail?.Definition,
      detail
    ),
  };
};

export const buildGenericCaseInput = ({
  definition,
  header,
  fieldValuesById,
}: {
  definition: CaseTypeDefinitionRecord;
  header: Partial<CaseInputPayload>;
  fieldValuesById: Record<string, string | string[]>;
}): CaseInputPayload => {
  const caseTypeId = getCaseTypeId(getCaseTypeFromDefinition(definition)) || 0;
  const fieldValues = getFieldsFromDefinition(definition)
    .map((field) => {
      if (!isCaseTypeFieldActive(field) || !isSupportedCaseField(field)) return;

      const fieldId = getCaseTypeFieldId(field);
      if (!fieldId) return;

      const fieldKey = String(fieldId);
      const input = buildCaseFieldValueInput(field, fieldValuesById[fieldKey], {
        includeEmpty: Object.prototype.hasOwnProperty.call(fieldValuesById, fieldKey),
      });
      return Array.isArray(input) ? input : input ? [input] : [];
    })
    .flat() as CaseFieldValueInput[];

  return {
    caseTypeId,
    propertyId: header.propertyId,
    propertyLoanId: header.propertyLoanId,
    orderNumber: header.orderNumber,
    workOrderId: header.workOrderId,
    openDt: header.openDt,
    dueDt: header.dueDt,
    followUpDt: header.followUpDt,
    completeDt: header.completeDt,
    assignedDt: header.assignedDt,
    vendorCode: header.vendorCode,
    assignedToUser: header.assignedToUser,
    caseStatusCode: header.caseStatusCode,
    clientCode: header.clientCode,
    fieldValues,
  };
};

export const getDocumentsFromCaseDetail = (detail?: CaseDetailRecord): CaseDocumentRecord[] =>
  asArray<CaseDocumentRecord>(detail?.documents || detail?.Documents);

export const valuesByCaseTypeFieldId = (detail?: CaseDetailRecord): Record<string, string> => {
  const values = asArray<CaseFieldValueRecord>(detail?.values || detail?.Values);
  const result: Record<string, string> = {};

  for (const value of values) {
    const fieldId = idFromRecord(asRecord(value), [
      'caseTypeFieldId',
      'CaseTypeFieldId',
      'case_type_field_id',
    ]);
    const display = getCaseFieldValue(value);
    if (fieldId && display) result[String(fieldId)] = display;
  }

  return result;
};

export const caseTypeFieldValuesById = (
  detail?: CaseDetailRecord,
  definition?: CaseTypeDefinitionRecord
): Record<string, string | string[]> => {
  const values = asArray<CaseFieldValueRecord>(detail?.values || detail?.Values);
  const fieldsById = new Map(
    getFieldsFromDefinition(definition).map((field) => [String(getCaseTypeFieldId(field)), field])
  );
  const occurrences = new Map<string, { occurrence: number; value: string }[]>();

  for (const value of values) {
    const record = asRecord(value);
    const fieldId = idFromRecord(record, [
      'caseTypeFieldId',
      'CaseTypeFieldId',
      'case_type_field_id',
    ]);
    const display = getCaseFieldValue(value);
    if (!fieldId || !display) continue;
    const key = String(fieldId);
    const occurrence = Number(firstValue(record, ['occurrence', 'Occurrence']) || 1);
    occurrences.set(key, [...(occurrences.get(key) || []), { occurrence, value: display }]);
  }

  return Object.fromEntries(
    Array.from(occurrences.entries()).map(([fieldId, entries]) => {
      const ordered = entries
        .sort((a, b) => a.occurrence - b.occurrence)
        .map((entry) => entry.value);
      return [
        fieldId,
        isCaseTypeFieldMultiple(fieldsById.get(fieldId)) ? ordered : ordered.at(-1) || '',
      ];
    })
  );
};

export const findDefinitionForCase = (
  definitions: CaseTypeDefinitionRecord[],
  header?: CaseHeaderRecord
) => {
  const caseTypeId = getCaseHeaderTypeId(header);
  if (!caseTypeId) return;

  return definitions.find(
    (definition) => getCaseTypeId(getCaseTypeFromDefinition(definition)) === caseTypeId
  );
};
